import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAllProducts } from '../utils/productService';
import Header from '../components/Header';
import SiteFooter from '../components/SiteFooter';
import FigmaProductCard from '../components/Products/FigmaProductCard';
import { useLanguage } from '../context/LanguageContext';
import { localizeProduct } from '../utils/localizeProduct';
import './Home.css';
import './Photobooks.css';

// Emrat vijnë nga src/i18n/*.js nën çelësat cat.*
const CATEGORY_IDS = ['wedding', 'travel', 'baby', 'agenda', 'kitchen', 'kids', 'frame'];

const PER_PAGE = 9;
// Pagination disabled per request - show every matching product on one page.
// Flip back to true (and the code below still works unchanged) to restore it.
const SHOW_PAGINATION = false;
// "Më të Shitura" tab disabled per request - only the Albumet (photobook)
// tab remains. Flip back to true to restore it, code is otherwise untouched.
const SHOW_BESTSELLER_TAB = false;

const Photobooks = () => {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('cat');
  const { t, lang } = useLanguage();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('photobook');
  const [selectedCategories, setSelectedCategories] = useState(initialCategory ? [initialCategory] : []);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [page, setPage] = useState(1);

  const bestsellerProducts = products.filter((p) => p.isBestseller);

  useEffect(() => {
    getAllProducts().then(setProducts).finally(() => setLoading(false));
  }, []);

  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const p of products) counts[p.category] = (counts[p.category] || 0) + 1;
    return counts;
  }, [products]);

  const toggleCategory = (catId) => {
    setPage(1);
    setSelectedCategories((prev) =>
      prev.includes(catId) ? prev.filter((c) => c !== catId) : [...prev, catId]
    );
  };

  const filteredProducts = useMemo(() => {
    let list = products;
    if (selectedCategories.length > 0) {
      list = list.filter((p) => selectedCategories.includes(p.category));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) => localizeProduct(p, lang).name.toLowerCase().includes(q));
    }
    const sorted = [...list];
    if (sortBy === 'price-asc') sorted.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-desc') sorted.sort((a, b) => b.price - a.price);
    else if (sortBy === 'name') sorted.sort((a, b) => localizeProduct(a, lang).name.localeCompare(localizeProduct(b, lang).name));
    return sorted;
  }, [products, selectedCategories, search, sortBy, lang]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageProducts = SHOW_PAGINATION
    ? filteredProducts.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE)
    : filteredProducts;

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="home-page">
      <Header />

      <div className="photobooks-tabs">
        {SHOW_BESTSELLER_TAB && (
          <button
            className={`photobooks-tab ${activeTab === 'bestseller' ? 'active' : ''}`}
            onClick={() => setActiveTab('bestseller')}
          >
            {t('catalog.bestsellers')}
          </button>
        )}
        <button
          className={`photobooks-tab ${activeTab === 'photobook' ? 'active' : ''}`}
          onClick={() => setActiveTab('photobook')}
        >
          {t('catalog.albums')}
        </button>
      </div>

      {SHOW_BESTSELLER_TAB && activeTab === 'bestseller' ? (
        <div className="photobooks-bestseller-section">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', width: '100%' }}>{t('catalog.loading')}</div>
          ) : bestsellerProducts.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', width: '100%' }}>{t('catalog.noBestsellers')}</div>
          ) : (
            <div className="figma-products-grid">
              {bestsellerProducts.map((product) => (
                <FigmaProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      ) : (
      <div className="photobooks-content">
        <aside className="photobooks-filters">
          <div className="photobooks-filters-header">
            <p>{t('catalog.options')}</p>
          </div>

          <div className="photobooks-search">
            <input
              type="text"
              placeholder={t('catalog.searchPlaceholder')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          <div className="photobooks-categories">
            {CATEGORY_IDS.map((catId) => (
              <label key={catId} className="photobooks-checkbox">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(catId)}
                  onChange={() => toggleCategory(catId)}
                />
                <span className="photobooks-checkbox-box" />
                <span className="photobooks-checkbox-label">
                  {t(`cat.${catId}`)} <span className="photobooks-checkbox-count">{categoryCounts[catId] || 0}</span>
                </span>
              </label>
            ))}
          </div>
        </aside>

        <main className="photobooks-main">
          <div className="photobooks-top">
            <p className="photobooks-selected-count">
              {t('catalog.selectedCount')} <strong>{filteredProducts.length}</strong>
            </p>
            <select
              className="photobooks-sort"
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            >
              <option value="default">{t('catalog.sortDefault')}</option>
              <option value="price-asc">{t('catalog.sortPriceAsc')}</option>
              <option value="price-desc">{t('catalog.sortPriceDesc')}</option>
              <option value="name">{t('catalog.sortName')}</option>
            </select>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', width: '100%' }}>{t('catalog.loading')}</div>
          ) : pageProducts.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', width: '100%' }}>{t('catalog.noResults')}</div>
          ) : (
            <div className="figma-products-grid">
              {pageProducts.map((product) => (
                <FigmaProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          {SHOW_PAGINATION && totalPages > 1 && (
            <div className="photobooks-pagination">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={p === currentPage ? 'active' : ''}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </button>
              ))}
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
            </div>
          )}
        </main>
      </div>
      )}

      <SiteFooter />
    </div>
  );
};

export default Photobooks;
